import { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Alert, App, Badge, Button, Card, Form, Input, Modal, Popconfirm, Select, Space,
  Table, Tag, Typography, Tooltip,
} from 'antd';
import {
  UserOutlined, ReloadOutlined, EditOutlined, CheckCircleOutlined,
  StopOutlined, MailOutlined, LockOutlined, DeleteOutlined,
} from '@ant-design/icons';

// No requiere useAuth directamente — la ruta ya protege por RoleRoute
import {
  getUsuarios, crearUsuario,
  resetPassword, toggleEstado, actualizarUsuario, eliminarUsuario,
} from '../../api/admin';
import { getCredenciales } from '../../api/credenciales';
import type { ClienteCredencial } from '../../api/credenciales';

// El backend entrega createdAt/lastModifiedAt en UTC (ISO con `Z`); sin el
// plugin utc no podemos convertir explícitamente a hora local.
dayjs.extend(utc);

const { Text, Title } = Typography;

interface Usuario {
  email: string;
  enabled: boolean;
  status: string;
  created: string;
  last_modified: string;
  nombre: string;
  nombre_empresa: string;
  rfc: string;
  role: string;
  last_login: string;
  groups: string[];
}

interface FormValues {
  email: string;
  nombre: string;
  nombre_empresa: string;
  role: string;
  rfc?: string | string[];
}

const GROUP_COLORS: Record<string, string> = {
  AdminGlobal: 'red',
  Contador: 'blue',
  Viewer: 'default',
};

const ROLE_LABEL: Record<string, string> = {
  AdminGlobal: 'Admin',
  Contador: 'Contador',
  Viewer: 'Cliente',
};

// Fuente única del select de roles: `value` = grupo Cognito, `label` = nombre
// visible, que debe coincidir con ROLE_LABEL de la tabla.
const ROLE_OPTIONS = [
  { value: 'AdminGlobal', label: ROLE_LABEL.AdminGlobal },
  { value: 'Contador', label: ROLE_LABEL.Contador },
  { value: 'Viewer', label: ROLE_LABEL.Viewer },
];

/** Fecha UTC (ISO) → hora local con offset explícito, p.ej. "01/01/2026 18:00 UTC-06:00". */
function formatearFecha(iso: string | null | undefined): string {
  if (!iso) return '—';
  const fecha = dayjs.utc(iso).local();
  return fecha.isValid() ? fecha.format('DD/MM/YYYY HH:mm [UTC]Z') : '—';
}

// El `message` del AxiosError es el genérico "Request failed..."; la causa real
// viaja en `response.data.error` (cors_err) o `response.data.message` (core).
function mensajeDeError(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: { error?: string; message?: string } } })
    ?.response?.data;
  const raw = data?.error || data?.message || (e instanceof Error ? e.message : '');
  // Cognito rechaza el reset en estados transitorios (FORCE_CHANGE_PASSWORD,
  // RESET_REQUIRED); traducimos el texto crudo a algo accionable.
  if (raw && /cannot be reset in the current state/i.test(raw)) {
    return 'No se puede resetear la contraseña en el estado actual del usuario.';
  }
  return raw || fallback;
}

export function GestionUsuarios() {
  const { message, modal } = App.useApp();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [form] = Form.useForm();
  const [detailOpen, setDetailOpen] = useState(false);
  const [clientes, setClientes] = useState<ClienteCredencial[]>([]);
  const roleValue = Form.useWatch('role', form);
  const needsRfc = roleValue !== 'AdminGlobal';
  const isContador = roleValue === 'Contador';

  const loadUsuarios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUsuarios(search);
      setUsuarios(res.usuarios);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Error cargando usuarios';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [search]);

  async function loadClientes() {
    try {
      const res = await getCredenciales();
      setClientes(res.clientes);
    } catch {
      // Silencioso — el select mostrará vacío si falla
    }
  }

  useEffect(() => {
    loadUsuarios();
  }, [loadUsuarios]);

  function handleResetPassword(email: string) {
    modal.confirm({
      title: 'Resetear contraseña',
      content: `Se enviará a ${email} un email con una contraseña temporal. ¿Deseas continuar?`,
      okText: 'Resetear',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await resetPassword(email);
          message.success('Email de reset enviado');
        } catch (e: unknown) {
          message.error(mensajeDeError(e, 'Error reseteando contraseña'));
        }
      },
    });
  }

  async function handleToggleEstado(email: string, enabled: boolean) {
    try {
      await toggleEstado(email, enabled);
      message.success(enabled ? 'Usuario habilitado' : 'Usuario deshabilitado');
      loadUsuarios();
    } catch (e: unknown) {
      message.error(mensajeDeError(e, 'Error cambiando estado'));
    }
  }

  async function handleEliminar(email: string) {
    try {
      await eliminarUsuario(email);
      message.success(`Usuario ${email} eliminado`);
      loadUsuarios();
    } catch (e: unknown) {
      message.error(mensajeDeError(e, 'Error eliminando usuario'));
    }
  }

  async function handleSubmitForm(values: FormValues) {
    try {
      const isAdmin = values.role === 'AdminGlobal';
      let rfcPayload: string | string[] = '';
      if (!isAdmin) {
        if (isContador && Array.isArray(values.rfc)) {
          rfcPayload = values.rfc;
        } else if (values.rfc) {
          rfcPayload = values.rfc;
        }
      }
      if (modalMode === 'create') {
        await crearUsuario({
          email: values.email,
          nombre: values.nombre,
          nombre_empresa: values.nombre_empresa,
          rfc: rfcPayload,
          role: values.role || 'Viewer',
        });
        message.success('Usuario creado. Se envió email con contraseña temporal.');
      } else {
        await actualizarUsuario(selectedUser!.email, {
          nombre: values.nombre,
          nombre_empresa: values.nombre_empresa,
          rfc: isAdmin ? '' : rfcPayload,
          role: values.role,
        });
        message.success('Usuario actualizado');
      }
      setModalOpen(false);
      form.resetFields();
      loadUsuarios();
    } catch (e: unknown) {
      message.error(mensajeDeError(e, 'Error guardando usuario'));
    }
  }

  async function openEdit(user: Usuario) {
    setSelectedUser(user);
    setModalMode('edit');
    const role = user.groups[0] || user.role || 'Viewer';
    const rfcs = user.rfc ? user.rfc.split(',').map(r => r.trim().toUpperCase()).filter(Boolean) : [];
    form.setFieldsValue({
      email: user.email,
      nombre: user.nombre,
      nombre_empresa: user.nombre_empresa,
      role,
      rfc: role === 'Contador' ? rfcs : (rfcs[0] ?? ''),
    });
    await loadClientes();
    setModalOpen(true);
  }

  async function openCreate() {
    setSelectedUser(null);
    setModalMode('create');
    form.resetFields();
    await loadClientes();
    setModalOpen(true);
  }

  const rfcOptions = clientes.map(c => ({
    value: c.rfc,
    label: `${c.rfc}${c.nombre_cliente ? '  —  ' + c.nombre_cliente : ''}`,
  }));

  const columns = [
    {
      title: 'Email',
      key: 'email',
      render: (_: unknown, u: Usuario) => (
        <Space>
          <MailOutlined />
          <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{u.email}</Text>
        </Space>
      ),
    },
    {
      title: 'Nombre',
      key: 'nombre',
      render: (_: unknown, u: Usuario) => u.nombre || '—',
    },
    {
      title: 'Empresa',
      key: 'empresa',
      render: (_: unknown, u: Usuario) => u.nombre_empresa || '—',
    },
    {
      title: 'RFC',
      key: 'rfc',
      render: (_: unknown, u: Usuario) => {
        const rfcs = u.rfc ? u.rfc.split(',').map(r => r.trim()).filter(Boolean) : [];
        if (rfcs.length === 0) return <Text>—</Text>;
        return (
          <Space size={4} wrap>
            {rfcs.map(r => (
              <Tag key={r} style={{ fontFamily: 'monospace', fontSize: 11 }}>{r}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Grupo',
      key: 'grupo',
      render: (_: unknown, u: Usuario) => {
        const grupo = u.groups[0] || u.role || 'Viewer';
        return <Tag color={GROUP_COLORS[grupo] || 'default'}>{ROLE_LABEL[grupo] ?? grupo}</Tag>;
      },
    },
    {
      title: 'Estado',
      key: 'estado',
      render: (_: unknown, u: Usuario) => (
        <Badge
          status={u.enabled ? 'success' : 'error'}
          text={u.enabled ? 'Activo' : 'Deshabilitado'}
        />
      ),
    },
    {
      title: 'Última Conexión',
      key: 'last_login',
      render: (_: unknown, u: Usuario) => (
        <Text type="secondary" style={{ fontSize: 11 }}>
          {formatearFecha(u.last_login)}
        </Text>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 220,
      render: (_: unknown, u: Usuario) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(u)} />
          </Tooltip>
          <Tooltip title="Resetear Contraseña">
            <Button size="small" icon={<LockOutlined />} onClick={() => handleResetPassword(u.email)} />
          </Tooltip>
          {u.enabled ? (
            <Popconfirm
              title="¿Deshabilitar usuario?"
              description={`${u.email} no podrá iniciar sesión hasta que lo habilites.`}
              onConfirm={() => handleToggleEstado(u.email, false)}
              okText="Deshabilitar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Deshabilitar">
                <Button size="small" danger icon={<StopOutlined />} />
              </Tooltip>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="¿Habilitar usuario?"
              description={`${u.email} podrá volver a iniciar sesión.`}
              onConfirm={() => handleToggleEstado(u.email, true)}
              okText="Habilitar"
              cancelText="Cancelar"
            >
              <Tooltip title="Habilitar">
                <Button size="small" icon={<CheckCircleOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
          <Tooltip title="Ver Detalle">
            <Button size="small" icon={<UserOutlined />} onClick={() => { setSelectedUser(u); setDetailOpen(true); }} />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar usuario?"
            description={`Esta acción no se puede deshacer. ¿Eliminar ${u.email}?`}
            onConfirm={() => handleEliminar(u.email)}
            okText="Eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginTop: 0, marginBottom: 16 }}>
        <UserOutlined /> Manejo de Usuarios
      </Title>

      {error && (
        <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />
      )}

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input.Search
            placeholder="Buscar por email o nombre..."
            allowClear
            onSearch={(v) => setSearch(v)}
            style={{ width: 300 }}
          />
          <Button type="primary" onClick={openCreate}>
            + Crear Usuario
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadUsuarios} loading={loading}>
            Recargar
          </Button>
        </Space>
      </Card>

      <Table
        rowKey="email"
        columns={columns}
        dataSource={usuarios}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        size="small"
      />

      {/* Modal Crear / Editar */}
      <Modal
        title={modalMode === 'create' ? 'Crear Usuario' : 'Editar Usuario'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
        okText={modalMode === 'create' ? 'Crear' : 'Guardar'}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitForm}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, type: 'email', message: 'Email válido requerido' },
              { max: 128, message: 'El email no puede superar los 128 caracteres' },
            ]}
          >
            <Input disabled={modalMode === 'edit'} />
          </Form.Item>
          <Form.Item name="nombre" label="Nombre">
            <Input />
          </Form.Item>
          <Form.Item name="nombre_empresa" label="Nombre de Empresa">
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Grupo/Rol" rules={[{ required: true, message: 'Selecciona un rol' }]}>
            <Select placeholder="Seleccionar grupo" options={ROLE_OPTIONS} />
          </Form.Item>
          {needsRfc && (
            <Form.Item
              name="rfc"
              label="RFC"
              rules={[{ required: true, message: 'Selecciona al menos un RFC' }]}
            >
              <Select
                placeholder="Seleccionar RFC"
                options={rfcOptions}
                mode={isContador ? 'multiple' : undefined}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Modal Detalle */}
      <Modal
        title="Detalle del Usuario"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
      >
        {selectedUser && (
          <div>
            <p><strong>Email:</strong> {selectedUser.email}</p>
            <p><strong>Nombre:</strong> {selectedUser.nombre || '—'}</p>
            <p><strong>Empresa:</strong> {selectedUser.nombre_empresa || '—'}</p>
            <p>
              <strong>RFC:</strong>{' '}
              {selectedUser.rfc
                ? selectedUser.rfc.split(',').map(r => r.trim()).filter(Boolean).join(', ')
                : '—'}
            </p>
            <p><strong>Grupos:</strong> {selectedUser.groups.map(g => ROLE_LABEL[g] ?? g).join(', ') || '—'}</p>
            <p><strong>Estado:</strong> {selectedUser.enabled ? 'Activo' : 'Deshabilitado'}</p>
            <p><strong>Status Cognito:</strong> {selectedUser.status}</p>
            <p><strong>Creado:</strong> {formatearFecha(selectedUser.created)}</p>
            <p><strong>Última Modificación:</strong> {formatearFecha(selectedUser.last_modified)}</p>
            <p><strong>Última Conexión:</strong> {formatearFecha(selectedUser.last_login)}</p>
          </div>
        )}
      </Modal>
    </div>
  );
}
